import Image from 'next/image';
import Link from 'next/link';

import { Box, Button, Flex, Heading, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/components/section-header';
import { trackEvent } from '~/features/analytics/track-event';
import type { SchoolContent } from '~/features/school/models/school-content';

import { SCHOOL_LP_ANALYTICS_EVENT } from '../constants';
import { CourseCollectionIcon } from '../icons';
import { SchoolLpContent } from '../models/school-lp-content';

/** The slice of a Sanity school subcategory the collection cards render. */
type SchoolSubcategory = Pick<
  NonNullable<SchoolContent['subcategories']>[number],
  'pageTitle' | 'slug' | 'pageSubtitle' | 'externalDescription'
>;

type CourseCollectionsSectionProps = NonNullable<StaticCourseCollectionsSectionProps>;

export function CourseCollectionsSection({
  eyebrow,
  title,
  description,
  ctaLabel,
  collections,
  theme,
}: CourseCollectionsSectionProps) {
  return (
    <Box as='section' bg='grey.50' padding={{ base: '64px 0', md: '96px 0' }}>
      <ContentContainer contentMaxWidth='content-max-width-lg'>
        <Flex alignItems='center' flexDirection='column' gap='48px'>
          <SectionHeader
            color='grey.500'
            containerProps={{ maxWidth: '842px' }}
            iconSlot={<CourseCollectionIcon boxSize='18px' color='grey.500' />}
            subtitle={eyebrow}
            descriptionSlot={
              <Text color='grey.500' fontSize={{ base: '0.875rem', md: '1rem' }} lineHeight='1.5'>
                {description}
              </Text>
            }
            titleSlot={
              <Heading as='h2' color='grey.900' size={{ base: 'mobile-heading-2', md: 'heading-2' }}>
                {title}
              </Heading>
            }
          />
          <Flex
            alignItems='center'
            flexWrap='wrap'
            gap={{ base: '32px', md: '48px' }}
            justifyContent='center'
            width='100%'
          >
            {collections.map((collection) => (
              <CollectionCard key={collection.href} collection={collection} ctaLabel={ctaLabel} theme={theme} />
            ))}
          </Flex>
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function CollectionCard({
  collection,
  ctaLabel,
  theme,
}: {
  collection: CourseCollectionsSectionProps['collections'][number];
  ctaLabel: string;
  theme: CourseCollectionsSectionProps['theme'];
}) {
  return (
    <Flex
      bg='white'
      border='1px solid'
      borderColor='grey.100'
      borderRadius='12px'
      flexDirection='column'
      gap='8px'
      maxWidth='304px'
      padding='8px'
      width={{ base: '100%', md: '304px' }}
    >
      <Flex alignItems='center' flexDirection='column'>
        <Flex
          alignItems='center'
          bgGradient={theme.cardGradient}
          borderRadius='8px'
          height='144px'
          overflow='hidden'
          paddingLeft='24px'
          position='relative'
          width='100%'
        >
          <Box height='144px' left={0} opacity={0.5} position='absolute' top={0} width='144px' aria-hidden>
            <Image alt='' height={144} src={theme.cardTextureImage} width={144} />
          </Box>
          <Flex
            alignItems='center'
            backdropFilter='blur(7.4px)'
            bg='rgba(0, 0, 0, 0.56)'
            borderRadius='4px'
            minHeight='72px'
            padding='16px'
            position='relative'
            width='216px'
          >
            <Heading as='h3' color='white' fontSize='1.3125rem' fontWeight='600' letterSpacing='-0.02em' lineHeight='1'>
              {collection.label}
            </Heading>
          </Flex>
        </Flex>
        <Box bg={theme.cardShadowColor} borderBottomRadius='5px' height='9px' opacity={0.4} width='89.5%' />
        <Box bg={theme.cardShadowColor} borderBottomRadius='3px' height='9px' opacity={0.24} width='84.2%' />
      </Flex>
      <Flex flexDirection='column' gap='24px' padding='12px'>
        <Flex flexDirection='column' gap='12px' paddingX='4px'>
          <Text color='grey.500' fontSize='0.75rem' lineHeight='1.4' noOfLines={4}>
            {collection.description}
          </Text>
        </Flex>
        <Button
          _hover={{ bg: theme.ctaHoverColor }}
          as={Link}
          bg={theme.ctaColor}
          color='white'
          display='flex'
          fontSize='0.8125rem'
          gap='10px'
          href={collection.href}
          paddingX='16px'
          paddingY='10px'
          onClick={() =>
            trackEvent({
              name: SCHOOL_LP_ANALYTICS_EVENT,
              type: 'click',
              label: 'course_collection_clicked',
              additionalAttributes: { collection: collection.label },
            })
          }
        >
          <CourseCollectionIcon boxSize='14px' />
          {ctaLabel}
        </Button>
      </Flex>
    </Flex>
  );
}

export function transformStaticCourseCollectionsSectionProps({
  content,
  subcategories,
}: {
  content: SchoolLpContent;
  subcategories: SchoolSubcategory[] | null | undefined;
}) {
  if (!content.courseCollections || !subcategories?.length) {
    return null;
  }

  const { eyebrow, title, description, ctaLabel } = content.courseCollections;

  return {
    eyebrow,
    title,
    description,
    ctaLabel,
    collections: subcategories.map((subcategory) => ({
      label: subcategory.pageTitle,
      description: subcategory.externalDescription ?? subcategory.pageSubtitle,
      href: `/course-collection/${subcategory.slug}`,
    })),
    theme: {
      cardGradient: content.theme.collections.cardGradient,
      cardTextureImage: content.theme.collections.cardTextureImage,
      cardShadowColor: content.theme.colors.accent,
      ctaColor: content.theme.colors.accentDark,
      ctaHoverColor: content.theme.colors.accent,
    },
  };
}

export type StaticCourseCollectionsSectionProps = ReturnType<typeof transformStaticCourseCollectionsSectionProps>;
